import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type { BankAccountOption, BankBalanceSummary, BankCurrency, MatchGroup, MoneyRow, PageResponse, ReconciliationRun } from "@/api/types";
import { useAuth } from "@/app/auth";
import { useWorkspace } from "@/app/workspace";
import { DomainShell, DownloadIcon, RulesIcon, useDomainWorkflow } from "@/components/DomainShell";
import { BankAccountsOverview, useBankAccountOverview, type BankOverviewStage } from "@/components/BankAccountsOverview";
import { useOptionalConfirmDialog } from "@/components/ConfirmDialog";
import { bankSearch } from "@/components/DomainContext";
import { ImportDetail, ImportWorkspace, type ImportWorkspaceConfig } from "@/components/ImportWorkspace";
import { MatchSpine } from "@/components/MatchSpine";
import { Empty, Panel } from "@/components/Page";
import { Pagination } from "@/components/Pagination";
import { useToast } from "@/components/Toast";
import { money, shortDate } from "@/components/format";
import { BankAggregationRulesModal, Provenance, ReviewWorkspace, ValidationReadiness } from "@/components/WorkflowViews";

const importConfig: ImportWorkspaceConfig = { domain: "BANK", accountKind: "BANK", apiBase: "/banking", basePath: "/bancos", noun: "bancos" };
type View = "reconciliation" | "data" | "imports" | "review";
type MovementScope = "through" | "period" | "prior";
type ManualSelection = Map<string, MoneyRow>;
type ManualSelectionSide = "BANK" | "LEDGER";
type ManualSelectionStatus = "EMPTY" | "CONFIGURATION_MISSING" | "AMOUNT_MISMATCH" | "DIRECTION_MISMATCH" | "READY";

type ManualSelectionTotals = {
  count: number;
  debit: bigint;
  credit: bigint;
  total: bigint;
  signed: bigint;
};

type ManualSelectionValidation = {
  status: ManualSelectionStatus;
  valid: boolean;
  difference: bigint;
  title: string;
  message: string;
};

const rowAmount = (row: MoneyRow) => {
  const debit = Number(row.debit ?? 0);
  return debit !== 0 ? debit : Number(row.credit ?? 0);
};
const absCents = (value: bigint) => value < 0n ? -value : value;
const valueToCents = (value: string | number | undefined): bigint => {
  const normalized = String(value ?? 0).trim().replace(",", ".");
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(normalized);
  if (!match) return 0n;
  const fraction = match[3] ?? "";
  const padded = fraction.padEnd(3, "0");
  let cents = BigInt(match[2]) * 100n + BigInt(padded.slice(0, 2));
  if (Number(padded[2]) >= 5) cents += 1n;
  return match[1] === "-" ? -cents : cents;
};
const centsToDecimal = (value: bigint): string => {
  const absolute = absCents(value);
  const sign = value < 0n ? "-" : "";
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
};
const centsMoney = (value: bigint, currency: string) => money(centsToDecimal(value), currency);
const BalancedCheckIcon = ({ className }: { className?: string }) => <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
  <path d="m5 12 4 4L19 6" />
</svg>;
const selectionTotals = (selection: ReadonlyMap<string, MoneyRow>, side: ManualSelectionSide): ManualSelectionTotals => {
  let debit = 0n;
  let credit = 0n;
  selection.forEach((row) => {
    const rowDebit = absCents(valueToCents(row.debit));
    const rowCredit = absCents(valueToCents(row.credit));
    debit += rowDebit;
    credit += rowCredit;
  });
  const signed = side === "BANK" ? credit - debit : debit - credit;
  return {
    count: selection.size,
    debit,
    credit,
    total: absCents(signed),
    signed,
  };
};
const validateManualSelection = (
  origin: ManualSelectionTotals,
  counterpart: ManualSelectionTotals,
  currency: string,
  maxDifference: bigint | null,
): ManualSelectionValidation => {
  const difference = absCents(origin.total - counterpart.total);
  if (!origin.count || !counterpart.count) {
    const message = !origin.count && !counterpart.count
      ? "Seleccioná al menos un movimiento bancario y un asiento del Libro Mayor."
      : !origin.count
        ? "Seleccioná al menos un movimiento bancario."
        : "Seleccioná al menos un asiento del Libro Mayor.";
    return { status: "EMPTY", valid: false, difference, title: "Armá la selección", message };
  }
  if (maxDifference === null) {
    return {
      status: "CONFIGURATION_MISSING",
      valid: false,
      difference,
      title: "Configuración no disponible",
      message: "No se pudo obtener el tope de diferencia permitido. Recargá la página antes de conciliar.",
    };
  }
  if (difference > maxDifference) {
    const missingSide = origin.total < counterpart.total ? "Extracto" : "Libro Mayor";
    const message = `Extracto ${centsMoney(origin.total, currency)} · Libro Mayor ${centsMoney(counterpart.total, currency)} · Diferencia ${centsMoney(difference, currency)}. Supera el tope permitido de ${centsMoney(maxDifference, currency)}; faltan ${centsMoney(difference, currency)} en ${missingSide}.`;
    return { status: "AMOUNT_MISMATCH", valid: false, difference, title: "La diferencia supera el tope", message };
  }
  const sameDirection = origin.signed === 0n || counterpart.signed === 0n
    ? origin.signed === counterpart.signed
    : (origin.signed < 0n) === (counterpart.signed < 0n);
  if (!sameDirection) {
    return {
      status: "DIRECTION_MISMATCH",
      valid: false,
      difference,
      title: "Sentido incompatible",
      message: "El sentido contable no coincide. Vinculá Débito de Extracto con Haber del Mayor, o Crédito de Extracto con Debe.",
    };
  }
  return {
    status: "READY",
    valid: true,
    difference,
    title: difference === 0n ? "Selección balanceada" : "Diferencia dentro del tope",
    message: difference === 0n
      ? `Los importes y el sentido contable coinciden. Tope permitido: ${centsMoney(maxDifference, currency)}.`
      : `La diferencia de ${centsMoney(difference, currency)} está dentro del tope permitido de ${centsMoney(maxDifference, currency)} y el sentido contable coincide.`,
  };
};
const toggleSelectedRow = (selection: ManualSelection, row: MoneyRow): ManualSelection => {
  const next = new Map(selection);
  if (next.has(row.id)) next.delete(row.id);
  else next.set(row.id, row);
  return next;
};
const toggleSelectedPage = (selection: ManualSelection, rows: MoneyRow[]): ManualSelection => {
  const next = new Map(selection);
  const allSelected = rows.length > 0 && rows.every((row) => next.has(row.id));
  rows.forEach((row) => {
    if (allSelected) next.delete(row.id);
    else next.set(row.id, row);
  });
  return next;
};
const qs = (values: Record<string, string | number>) => new URLSearchParams(Object.entries(values).filter(([, value]) => value !== "").map(([key, value]) => [key, String(value)])).toString();
const previousMonthEnd = (period: string) => {
  const date = new Date(`${period}-01T00:00:00Z`);
  date.setUTCDate(0);
  return date.toISOString().slice(0, 10);
};
const isMonthEndDate = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.valueOf()) || date.toISOString().slice(0, 10) !== value) return false;
  date.setUTCDate(date.getUTCDate() + 1);
  return date.getUTCDate() === 1;
};
const balanceMoney = (value: string | null, currency: BankCurrency) => value === null ? "Dato faltante" : money(value, currency);

function BankBannerActions({ onOpenExport, onOpenRules }: { onOpenExport(): void; onOpenRules(): void }) {
  return <><button type="button" className="context-action" onClick={onOpenExport}><DownloadIcon/><span>Exportar</span></button><button type="button" className="context-action" onClick={onOpenRules}><RulesIcon/><span>Reglas de agrupación</span></button></>;
}

function BankExportModal({ open, initialBankId, initialCurrency, onClose }: { open: boolean; initialBankId: string; initialCurrency: string; onClose(): void }) {
  const { hotelId, period } = useWorkspace();
  const dialog = useRef<HTMLElement>(null);
  const firstField = useRef<HTMLSelectElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [bankId, setBankId] = useState("");
  const [currency, setCurrency] = useState<BankCurrency | "">("");
  const [periodFrom, setPeriodFrom] = useState(period);
  const [periodTo, setPeriodTo] = useState(period);
  const accounts = useQuery({
    queryKey: ["bank-account-options", "export", hotelId],
    queryFn: (): Promise<BankAccountOption[]> => responseData(apiClient.GET("/api/v1/banking/accounts", { params: { query: { hotelId, includeHistorical: "true" } } })),
    enabled: open && Boolean(hotelId),
  });
  const accountOptions = Array.isArray(accounts.data) ? accounts.data : [];
  const bankOptions = [...new Map(accountOptions.map((account) => [account.bankId, { id: account.bankId, name: account.bankName }])).values()];
  const selectedAccounts = accountOptions.filter((account) => account.bankId === bankId);
  const currencyOptions = [...new Set(selectedAccounts.map((account) => account.currency))].map((item) => ({
    currency: item,
    active: selectedAccounts.some((account) => account.currency === item && account.active),
    historical: selectedAccounts.every((account) => account.currency !== item || account.historical),
  }));
  const rangeError = !/^\d{4}-(0[1-9]|1[0-2])$/.test(periodFrom) || !/^\d{4}-(0[1-9]|1[0-2])$/.test(periodTo)
    ? "Completá ambos meses para exportar."
    : periodFrom.slice(0, 4) !== periodTo.slice(0, 4)
      ? "El período desde y hasta debe pertenecer al mismo año."
      : periodFrom > periodTo
        ? "El período desde no puede ser posterior al período hasta."
        : "";
  const exportUrl = bankId && currency && !rangeError
    ? `/api/v1/banking/reports/reconciliation.xlsx?${qs({ hotelId, periodFrom, periodTo, bankId, currency })}`
    : undefined;

  useEffect(() => {
    if (!open) return;
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => firstField.current?.focus());
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); return; }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), input:not(:disabled), a[href]') ?? []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown);
      const target = returnFocus.current;
      if (target?.closest("[hidden]")) document.querySelector<HTMLElement>('[aria-label="Abrir acciones de Bancos"]')?.focus();
      else target?.focus();
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !accounts.data) return;
    const selectedBank = accountOptions.some((account) => account.bankId === initialBankId) ? initialBankId : "";
    const selectedCurrency = accountOptions.some((account) => account.bankId === selectedBank && account.currency === initialCurrency)
      ? initialCurrency as BankCurrency
      : "";
    setBankId(selectedBank);
    setCurrency(selectedCurrency);
    setPeriodFrom(period);
    setPeriodTo(period);
  }, [accounts.data, initialBankId, initialCurrency, open, period]);
  useEffect(() => {
    if (open && !accounts.isPending && !dialog.current?.contains(document.activeElement)) firstField.current?.focus();
  }, [accounts.isPending, open]);

  if (!open) return null;
  const selectBank = (nextBankId: string) => {
    setBankId(nextBankId);
    if (!accountOptions.some((account) => account.bankId === nextBankId && account.currency === currency)) setCurrency("");
  };
  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={dialog} className="import-modal bank-export-modal" role="dialog" aria-modal="true" aria-labelledby="bank-export-title">
      <header><div><small>Reporte bancario · {periodFrom || period}</small><h2 id="bank-export-title">Exportar conciliación</h2></div><button type="button" className="modal-close" onClick={onClose} aria-label="Cerrar modal">×</button></header>
      <div className="stack-form bank-export-form">
        <fieldset className="bank-export-period-range"><legend>Período del reporte</legend><label>Desde<input type="month" value={periodFrom} onChange={(event) => setPeriodFrom(event.target.value)} required /></label><span aria-hidden="true">→</span><label>Hasta<input type="month" value={periodTo} onChange={(event) => setPeriodTo(event.target.value)} required /></label></fieldset>
        <div className="form-row">
          <label>Banco<select ref={firstField} required value={bankId} disabled={accounts.isPending} onChange={(event) => selectBank(event.target.value)}><option value="">{accounts.isPending ? "Cargando bancos…" : "Seleccionar banco"}</option>{bankOptions.map((bank) => <option key={bank.id} value={bank.id}>{bank.name}</option>)}</select></label>
          <label>Moneda<select required value={currency} disabled={!bankId} onChange={(event) => setCurrency(event.target.value as BankCurrency | "")}><option value="">Seleccionar moneda</option>{currencyOptions.map((item) => <option key={item.currency} value={item.currency}>{item.currency === "ARS" ? "Pesos (ARS)" : "Dólares (USD)"}{item.active ? "" : " · Inactiva"}{item.historical ? " · Histórica" : ""}</option>)}</select></label>
        </div>
        {accounts.isError && <div className="message message--error">No se pudieron cargar los bancos disponibles.</div>}
        {rangeError && <div className="message message--error">{rangeError}</div>}
        <div className="bank-export-actions"><button type="button" className="button button--ghost" onClick={onClose}>Cancelar</button><a className={`button button--primary${exportUrl ? "" : " button--disabled"}`} href={exportUrl} aria-disabled={!exportUrl} onClick={(event) => { if (!exportUrl) { event.preventDefault(); return; } onClose(); }}><DownloadIcon/><span>Descargar Excel</span></a></div>
      </div>
    </section>
  </div>;
}

function BankBalanceBridge({ summary, canEdit, onEdit }: { summary: BankBalanceSummary; canEdit: boolean; onEdit(): void }) {
  const missingMessages = [
    summary.missingInputs.includes("ANCHOR") ? "Cargá el saldo inicial al cierre del mes anterior." : "",
    summary.missingInputs.includes("LEDGER_PERIODS")
      ? `Cargá el Libro Mayor de ${summary.missingLedgerPeriods.length ? summary.missingLedgerPeriods.join(", ") : summary.period}.`
      : "",
    summary.missingInputs.includes("STATEMENT_BALANCE")
      ? "Reemplazá el extracto: no se detectó un saldo firmado."
      : "",
  ].filter(Boolean);
  const amount = (value: string | null) => <strong className={value === null ? "is-missing" : ""}>{balanceMoney(value, summary.currency)}</strong>;
  return <section className="bank-balance-bridge" aria-labelledby="bank-balance-title">
    <header>
      <div><small>Resumen · {summary.period}</small><h2 id="bank-balance-title">{summary.bankName} · {summary.currency}</h2></div>
      <div className="bank-balance-anchor">
        <span>{summary.anchor ? `Saldo inicial al ${shortDate(summary.anchor.asOfDate)}` : "Sin saldo inicial"}</span>
        {canEdit && <button type="button" className="button button--small button--ghost" onClick={onEdit}>{summary.anchor ? "Corregir saldo inicial" : "Cargar saldo inicial"}</button>}
      </div>
    </header>
    <div className="bank-balance-equation" aria-label="Cálculo del saldo contable al cierre">
      <article><small>Saldo anterior</small>{amount(summary.ledger.openingBalance)}</article>
      <span className="bank-balance-operator" aria-hidden="true">+</span>
      <article><small>Debe</small>{amount(summary.ledger.debit)}</article>
      <span className="bank-balance-operator" aria-hidden="true">−</span>
      <article><small>Haber</small>{amount(summary.ledger.credit)}</article>
      <span className="bank-balance-operator" aria-hidden="true">=</span>
      <article className="bank-balance-result"><small>Saldo contable</small>{amount(summary.ledger.closingBalance)}<span>Variación {balanceMoney(summary.ledger.netMovement, summary.currency)}</span></article>
    </div>
    <div className="bank-balance-reconciliation">
      <article><small>Ajuste pendiente</small>{amount(summary.pending.netAdjustment)}<span>Créditos pendientes − débitos pendientes</span></article>
      <span className="bank-balance-operator" aria-hidden="true">→</span>
      <article className="bank-balance-result"><small>Saldo conciliado</small>{amount(summary.reconciledBalance)}<span>Saldo contable + ajuste</span></article>
      <article><small>Saldo del extracto</small>{amount(summary.statementBalance)}<span>{summary.statementBalanceDate ? `Firmado al ${shortDate(summary.statementBalanceDate)}` : "Sin saldo detectable"}</span></article>
      <article className={`bank-balance-difference${summary.difference !== null && Number(summary.difference) !== 0 ? " has-difference" : ""}`}><small>Diferencia</small>{amount(summary.difference)}<span>Conciliado − extracto</span></article>
    </div>
    {missingMessages.length > 0 && <div className="bank-balance-missing" role="status"><b>Datos incompletos</b><ul>{missingMessages.map((message) => <li key={message}>{message}</li>)}</ul></div>}
  </section>;
}

function BankBalanceAnchorModal({ open, summary, onClose }: { open: boolean; summary: BankBalanceSummary; onClose(): void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const firstField = useRef<HTMLInputElement>(null);
  const [asOfDate, setAsOfDate] = useState(previousMonthEnd(summary.period));
  const [amount, setAmount] = useState("");
  const latestAllowedDate = previousMonthEnd(summary.period);
  const dateIsMonthEnd = isMonthEndDate(asOfDate);
  const error = !dateIsMonthEnd
    ? "La fecha debe ser el último día de un mes."
    : asOfDate > latestAllowedDate
      ? "La fecha debe ser anterior al período consultado."
      : !/^[+-]?\d+(?:[.,]\d{1,2})?$/.test(amount.trim())
        ? "Ingresá un importe válido, con signo si corresponde."
        : "";
  const save = useMutation({
    mutationFn: () => responseData(apiClient.PUT("/api/v1/banking/balance-anchor", { body: {
      hotelId: summary.hotelId,
      bankId: summary.bankId,
      currency: summary.currency,
      asOfDate,
      amount: amount.trim().replace(",", "."),
    } })),
    onSuccess: async () => {
      toast.success(summary.anchor ? "Saldo inicial corregido." : "Saldo inicial cargado.");
      await queryClient.invalidateQueries({ queryKey: ["bank-balance-summary", summary.hotelId] });
      onClose();
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo guardar el saldo inicial." }),
  });
  useEffect(() => {
    if (!open) return;
    setAsOfDate(summary.anchor?.asOfDate ?? latestAllowedDate);
    setAmount(summary.anchor?.amount ?? "");
    requestAnimationFrame(() => firstField.current?.focus());
  }, [latestAllowedDate, open, summary.anchor?.amount, summary.anchor?.asOfDate]);
  useEffect(() => {
    if (!open) return;
    const keydown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", keydown);
    return () => document.removeEventListener("keydown", keydown);
  }, [onClose, open]);
  if (!open) return null;
  const submit = (event: FormEvent) => { event.preventDefault(); if (!error) save.mutate(); };
  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="import-modal bank-anchor-modal" role="dialog" aria-modal="true" aria-labelledby="bank-anchor-title">
      <header><div><small>{summary.bankName} · {summary.currency}</small><h2 id="bank-anchor-title">{summary.anchor ? "Corregir saldo inicial" : "Cargar saldo inicial"}</h2></div><button type="button" className="modal-close" onClick={onClose} aria-label="Cerrar modal">×</button></header>
      <form className="stack-form" onSubmit={submit}>
        <p className="bank-anchor-explanation">Ingresá el saldo contable acumulado al cierre del mes anterior. Los períodos posteriores arrastrarán sólo el saldo contable, sin partidas pendientes.</p>
        <div className="form-row"><label>Fecha de cierre<input ref={firstField} type="date" value={asOfDate} max={latestAllowedDate} onChange={(event) => setAsOfDate(event.target.value)} required /></label><label>Importe con signo<input className="mono" type="text" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="-62582,70" required /></label></div>
        {error && <div className="message message--error">{error}</div>}
        <div className="bank-export-actions"><button type="button" className="button button--ghost" onClick={onClose}>Cancelar</button><button type="submit" className="button button--primary" disabled={Boolean(error) || save.isPending}>{save.isPending ? "Guardando…" : "Guardar saldo"}</button></div>
      </form>
    </section>
  </div>;
}

function ManualSelectionProofSide({
  label,
  totals,
  currency,
  debitLabel,
  creditLabel,
}: {
  label: string;
  totals: ManualSelectionTotals;
  currency: string;
  debitLabel: string;
  creditLabel: string;
}) {
  return <div className="manual-selection-proof__side">
    <div className="manual-selection-proof__heading">
      <span>{label}</span>
      <b>{totals.count} {totals.count === 1 ? "seleccionado" : "seleccionados"}</b>
    </div>
    <strong className="manual-selection-proof__total mono">{centsMoney(totals.total, currency)}</strong>
    <div className="manual-selection-proof__breakdown">
      <span>{debitLabel} <b className="mono">{centsMoney(totals.debit, currency)}</b></span>
      <span>{creditLabel} <b className="mono">{centsMoney(totals.credit, currency)}</b></span>
    </div>
  </div>;
}

function SelectedManualRows({
  title,
  side,
  selection,
  disabled,
  onRemove,
  onClear,
}: {
  title: string;
  side: ManualSelectionSide;
  selection: ReadonlyMap<string, MoneyRow>;
  disabled: boolean;
  onRemove(row: MoneyRow): void;
  onClear(): void;
}) {
  const rows = [...selection.values()];
  return <section className="manual-selection-detail__column" aria-label={`Selección de ${title}`}>
    <header>
      <div><small>{title}</small><strong>{rows.length} {rows.length === 1 ? "movimiento" : "movimientos"}</strong></div>
      <button type="button" className="button button--small" disabled={!rows.length || disabled} onClick={onClear}>Limpiar {title}</button>
    </header>
    {rows.length ? <div className="manual-selection-detail__list">
      {rows.map((row) => {
        const debit = valueToCents(row.debit) !== 0n;
        const direction = side === "LEDGER" ? (debit ? "Debe" : "Haber") : (debit ? "Débito" : "Crédito");
        const description = row.description || "Sin descripción";
        return <article className="manual-selection-detail__row" key={row.id}>
          <div>
            <strong>{description}</strong>
            <span><time className="mono" dateTime={row.transactionDate}>{shortDate(row.transactionDate)}</time> · {direction}</span>
          </div>
          <b className="mono">{money(rowAmount(row), row.currency)}</b>
          <button type="button" disabled={disabled} onClick={() => onRemove(row)} aria-label={`Quitar ${description} de ${title}`}>Quitar</button>
        </article>;
      })}
    </div> : <p className="manual-selection-detail__empty">Todavía no seleccionaste movimientos de este lado.</p>}
  </section>;
}

function ManualSelectionBalance({
  movements,
  ledgerEntries,
  movementTotals,
  ledgerTotals,
  validation,
  currency,
  disabled,
  onRemoveMovement,
  onRemoveLedger,
  onClearMovements,
  onClearLedger,
  onClearAll,
}: {
  movements: ReadonlyMap<string, MoneyRow>;
  ledgerEntries: ReadonlyMap<string, MoneyRow>;
  movementTotals: ManualSelectionTotals;
  ledgerTotals: ManualSelectionTotals;
  validation: ManualSelectionValidation;
  currency: string;
  disabled: boolean;
  onRemoveMovement(row: MoneyRow): void;
  onRemoveLedger(row: MoneyRow): void;
  onClearMovements(): void;
  onClearLedger(): void;
  onClearAll(): void;
}) {
  const totalSelected = movements.size + ledgerEntries.size;
  const proofValue = validation.status === "DIRECTION_MISMATCH"
      ? "Importes iguales"
      : validation.status === "AMOUNT_MISMATCH"
        ? centsMoney(validation.difference, currency)
        : "—";
  return <section className={`manual-selection-balance manual-selection-balance--${validation.status.toLowerCase()}`} aria-label="Balance de conciliación manual">
    <div className="manual-selection-proof">
      <ManualSelectionProofSide label="Extracto" totals={movementTotals} currency={currency} debitLabel="Débitos" creditLabel="Créditos" />
      <div className="manual-selection-proof__control" role="status" aria-live="polite" aria-atomic="true">
        <small>{validation.title}</small>
        {validation.status === "READY"
          ? <strong className="manual-selection-proof__check"><BalancedCheckIcon /></strong>
          : <strong className="mono">{proofValue}</strong>}
        <p>{validation.message}</p>
      </div>
      <ManualSelectionProofSide label="Libro Mayor" totals={ledgerTotals} currency={currency} debitLabel="Debe" creditLabel="Haber" />
    </div>
    {totalSelected > 0 && <details className="manual-selection-detail">
      <summary>
        <span>Revisar selección</span>
        <b>{totalSelected} {totalSelected === 1 ? "registro" : "registros"}</b>
      </summary>
      <div className="manual-selection-detail__content">
        <SelectedManualRows title="Extracto" side="BANK" selection={movements} disabled={disabled} onRemove={onRemoveMovement} onClear={onClearMovements} />
        <SelectedManualRows title="Libro Mayor" side="LEDGER" selection={ledgerEntries} disabled={disabled} onRemove={onRemoveLedger} onClear={onClearLedger} />
      </div>
      <footer><button type="button" className="button button--small" disabled={disabled} onClick={onClearAll}>Limpiar toda la selección</button></footer>
    </details>}
  </section>;
}

export function BanksPage({ view = "reconciliation" }: { view?: View }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useOptionalConfirmDialog();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hotelId, period } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const movementPage = Number(params.get("movementPage") ?? 1);
  const ledgerPage = Number(params.get("ledgerPage") ?? 1);
  const groupPage = Number(params.get("groupPage") ?? 1);
  const scopeParam = params.get("scope");
  const scope: MovementScope = scopeParam === "period" || scopeParam === "prior" ? scopeParam : "through";
  const q = view === "data" ? params.get("q") ?? "" : "";
  const sourceId = params.get("sourceId") ?? "";
  const accountId = params.get("accountId") ?? "";
  const batchId = params.get("batchId") ?? "";
  const bankId = params.get("bankId") ?? params.get("masterId") ?? "";
  const currency = params.get("currency") ?? "";
  const aggregateScope = !bankId || !currency;
  const contextSearch = bankSearch(bankId, currency);
  const currentSearch = params.toString();
  const [search, setSearch] = useState(q);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [anchorOpen, setAnchorOpen] = useState(false);
  const closeExport = useCallback(() => setExportOpen(false), []);
  const [selectedMovements, setSelectedMovements] = useState<ManualSelection>(() => new Map());
  const [selectedLedgerEntries, setSelectedLedgerEntries] = useState<ManualSelection>(() => new Map());
  const [selectionSummaryOpen, setSelectionSummaryOpen] = useState(false);
  const workflow = useDomainWorkflow(importConfig);
  const hasWorkflowSources = Boolean(workflow.data?.sources.length);
  const selectedCurrency: BankCurrency | undefined = currency === "ARS" || currency === "USD" ? currency : undefined;
  const accountOverview = useBankAccountOverview(hotelId, period, bankId, currency, aggregateScope);
  const overviewStage: BankOverviewStage = view === "imports" ? "load" : view === "data" ? "validate" : view === "review" ? "review" : "reconcile";
  const balanceSummary = useQuery({
    queryKey: ["bank-balance-summary", hotelId, period, bankId, selectedCurrency],
    queryFn: (): Promise<BankBalanceSummary> => responseData(apiClient.GET("/api/v1/banking/balance-summary", { params: { query: { hotelId, period, bankId, currency: selectedCurrency! } } })),
    enabled: view === "review" && Boolean(hotelId && bankId && selectedCurrency),
  });
  const run = useQuery({ queryKey: ["run", "bank", hotelId, period], queryFn: async (): Promise<ReconciliationRun | null> => (await responseData(apiClient.GET("/api/v1/banking/reconciliations/latest", { params: { query: { hotelId, period } } }))) ?? null, enabled: Boolean(hotelId) && (view === "reconciliation" || view === "review" || hasWorkflowSources) });
  const movements = useQuery({
    queryKey: ["bank-movements", hotelId, period, movementPage, scope, q, bankId, currency],
    queryFn: (): Promise<PageResponse<MoneyRow>> => responseData(apiClient.GET("/api/v1/banking/movements", { params: { query: { hotelId, period, page: String(movementPage), pageSize: "25", scope, q, bankId: bankId || undefined, currency: currency === "ARS" || currency === "USD" ? currency : undefined } } })),
    enabled: !aggregateScope && Boolean(hotelId) && (view === "reconciliation" || view === "data")
  });
  const ledger = useQuery({
    queryKey: ["bank-ledger", hotelId, period, ledgerPage, scope, q, bankId, currency],
    queryFn: (): Promise<PageResponse<MoneyRow>> => responseData(apiClient.GET("/api/v1/banking/ledger-entries", { params: { query: { hotelId, period, page: String(ledgerPage), pageSize: "25", scope, q, bankId: bankId || undefined, currency: currency === "ARS" || currency === "USD" ? currency : undefined } } })),
    enabled: !aggregateScope && Boolean(hotelId) && (view === "reconciliation" || view === "data")
  });
  const groups = useQuery({
    queryKey: ["groups", "bank", run.data?.id, groupPage, bankId, currency],
    queryFn: (): Promise<PageResponse<MatchGroup>> => responseData(apiClient.GET("/api/v1/banking/reconciliations/{id}/groups", { params: { path: { id: run.data!.id }, query: { page: String(groupPage), pageSize: "20", status: "AUTO", bankId: bankId || undefined, currency: currency === "ARS" || currency === "USD" ? currency : undefined } } })),
    enabled: !aggregateScope && view === "reconciliation" && Boolean(run.data?.id)
  });
  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ["bank-movements", hotelId, period] }),
    queryClient.invalidateQueries({ queryKey: ["bank-ledger", hotelId, period] }),
    queryClient.invalidateQueries({ queryKey: ["run", "bank", hotelId, period] }),
    queryClient.invalidateQueries({ queryKey: ["groups", "bank"] }),
    queryClient.invalidateQueries({ queryKey: ["review-groups"] }),
    queryClient.invalidateQueries({ queryKey: ["workflow", "BANK"] }),
    queryClient.invalidateQueries({ queryKey: ["bank-review-summary", hotelId, period] }),
    queryClient.invalidateQueries({ queryKey: ["bank-balance-summary", hotelId] }),
    queryClient.invalidateQueries({ queryKey: ["bank-account-overview", hotelId, period] })
  ]);
  const execute = useMutation({
    mutationFn: () => responseData(apiClient.POST("/api/v1/banking/reconciliations", { body: { hotelId, period, bankId: bankId || undefined, currency: currency === "ARS" || currency === "USD" ? currency : undefined } })),
    onSuccess: async (result) => {
      const processed = result.processedAccounts?.length ?? 0;
      const skipped = result.skippedAccounts?.length ?? 0;
      toast.success(aggregateScope ? `${processed} cuenta${processed === 1 ? "" : "s"} conciliada${processed === 1 ? "" : "s"}${skipped ? ` · ${skipped} omitida${skipped === 1 ? "" : "s"}` : ""}.` : "Conciliación bancaria ejecutada.");
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ["workflow", "BANK"] });
      navigate(`/bancos/revision${contextSearch}`);
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo ejecutar la conciliación bancaria." })
  });
  const reconcile = async () => {
    if (!aggregateScope) { execute.mutate(); return; }
    const accounts = accountOverview.data?.accounts ?? [];
    const ready = accounts.filter((account) => account.stages.validate === "COMPLETE");
    const skipped = accounts.filter((account) => account.stages.validate !== "COMPLETE");
    const readyNames = ready.map((account) => `${account.bankName} · ${account.currency}`).join(", ");
    const skippedNames = skipped.map((account) => {
      const missing = [account.loads.extractStatus ? "" : "Extracto", account.loads.ledgerStatus ? "" : "Libro Mayor"].filter(Boolean).join(" y ");
      return `${account.bankName} · ${account.currency}${missing ? ` (${missing})` : " (requiere validación)"}`;
    }).join(", ");
    const accepted = confirm ? await confirm({
      title: `Conciliar ${ready.length} cuenta${ready.length === 1 ? "" : "s"} lista${ready.length === 1 ? "" : "s"}`,
      description: `Se procesarán: ${readyNames || "ninguna"}.${skippedNames ? ` Se omitirán: ${skippedNames}.` : ""}`,
      confirmLabel: "Ejecutar conciliación",
    }) : false;
    if (accepted) execute.mutate();
  };
  const manualCurrency = selectedCurrency ?? selectedMovements.values().next().value?.currency ?? selectedLedgerEntries.values().next().value?.currency ?? "ARS";
  const movementSelectionTotals = selectionTotals(selectedMovements, "BANK");
  const ledgerSelectionTotals = selectionTotals(selectedLedgerEntries, "LEDGER");
  const configuredMaxDifference = workflow.data?.maxApprovableDifference;
  const manualMaxDifference = configuredMaxDifference === undefined ? null : absCents(valueToCents(configuredMaxDifference));
  const manualSelectionValidation = validateManualSelection(movementSelectionTotals, ledgerSelectionTotals, manualCurrency, manualMaxDifference);
  const manualSelectionCount = selectedMovements.size + selectedLedgerEntries.size;
  const manualSelectionCountLabel = `${manualSelectionCount} ${manualSelectionCount === 1 ? "seleccionado" : "seleccionados"}`;
  const manualSelectionToggleLabel = manualSelectionCount === 0
    ? "Selección · 0"
    : manualSelectionValidation.status === "READY"
      ? `${manualSelectionCountLabel} ·`
      : manualSelectionValidation.status === "AMOUNT_MISMATCH"
        ? `${manualSelectionCountLabel} · Dif. ${centsMoney(manualSelectionValidation.difference, manualCurrency)}`
        : manualSelectionValidation.status === "DIRECTION_MISMATCH"
          ? `${manualSelectionCountLabel} · Sentido incompatible`
          : manualSelectionValidation.status === "CONFIGURATION_MISSING"
            ? `${manualSelectionCountLabel} · Tope no disponible`
          : selectedMovements.size === 0
            ? `${manualSelectionCountLabel} · Falta Extracto`
            : `${manualSelectionCountLabel} · Falta Libro Mayor`;
  const manualSelectionToggleDescription = `Resumen de selección manual: ${selectedMovements.size} ${selectedMovements.size === 1 ? "movimiento" : "movimientos"} del Extracto y ${selectedLedgerEntries.size} ${selectedLedgerEntries.size === 1 ? "registro" : "registros"} del Libro Mayor. ${manualSelectionValidation.message} ${selectionSummaryOpen ? "Contraer resumen." : "Expandir resumen."}`;
  const manualAmountComparison = `Extracto ${centsMoney(movementSelectionTotals.total, manualCurrency)} · Libro Mayor ${centsMoney(ledgerSelectionTotals.total, manualCurrency)} · Diferencia ${centsMoney(manualSelectionValidation.difference, manualCurrency)}.`;
  const movementIds = [...selectedMovements.keys()];
  const ledgerIds = [...selectedLedgerEntries.keys()];
  const manual = useMutation({
    mutationFn: () => responseData(apiClient.POST("/api/v1/banking/reconciliations/manual", { body: { hotelId, period, movementIds, ledgerIds } })),
    onSuccess: async (result) => {
      toast.success(result.status === "SUGGESTED" ? "Conciliación manual enviada a revisión." : "Conciliación manual guardada.");
      setSelectedMovements(new Map());
      setSelectedLedgerEntries(new Map());
      setSelectionSummaryOpen(false);
      await refresh();
    },
    onError: (reason) => {
      if (reason instanceof Error && reason.message.includes("supera el tope permitido")) {
        toast.error(new Error(`${reason.message} ${manualAmountComparison}`));
        return;
      }
      toast.error(reason, { fallback: "No se pudo guardar la conciliación manual." });
    }
  });
  const setPage = (key: string, page: number) => { const next = new URLSearchParams(params); next.set(key, String(page)); setParams(next); };
  const applyDataFilters = (values: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
    if ("scope" in values) next.delete("status");
    next.set("movementPage", "1");
    next.set("ledgerPage", "1");
    setParams(next);
  };
  useEffect(() => {
    setSelectedMovements(new Map());
    setSelectedLedgerEntries(new Map());
    setSelectionSummaryOpen(false);
  }, [bankId, currency, period]);
  useEffect(() => {
    if (aggregateScope || view !== "review" || !run.isSuccess || run.data) return;
    navigate(`/bancos/movimientos${currentSearch ? `?${currentSearch}` : ""}`, { replace: true });
  }, [aggregateScope, currentSearch, navigate, run.data, run.isSuccess, view]);
  const error = accountOverview.error ?? run.error ?? movements.error ?? ledger.error ?? groups.error ?? balanceSummary.error;
  const processingAccountCount = aggregateScope ? accountOverview.data?.summary?.readyCount ?? 0 : 1;
  const processingAccountLabel = `${processingAccountCount} cuenta${processingAccountCount === 1 ? "" : "s"} lista${processingAccountCount === 1 ? "" : "s"}`;

  return (
    <>
      <DomainShell
      eyebrow="Extracto contra contabilidad" title="Bancos"
      description="Cruce de extractos y mayores con arrastre de movimientos pendientes."
      basePath="/bancos" dataPath="movimientos"
      importConfig={importConfig}
      compactContext
      onReconcile={reconcile}
      reconciling={execute.isPending}
      actions={
        <BankBannerActions
          onOpenExport={() => setExportOpen(true)}
          onOpenRules={() => setRulesOpen(true)}
        />
      }
    >
      {execute.isPending && <section className="bank-rules-processing" role="status" aria-live="polite" aria-label="Conciliación bancaria en proceso">
        <div className="bank-rules-processing__match" aria-hidden="true">
          <span className="bank-rules-processing__ledger"><i /><i /><i /></span>
          <b>↔</b>
          <span className="bank-rules-processing__ledger"><i /><i /><i /></span>
        </div>
        <div className="bank-rules-processing__copy">
          <small>Conciliación en proceso</small>
          <strong>Ejecutando reglas de conciliación</strong>
          <span>Estamos comparando el Extracto y el Libro Mayor de {processingAccountLabel}. Los resultados se mostrarán al finalizar.</span>
        </div>
        <span className="bank-rules-processing__track" aria-hidden="true"><i /></span>
      </section>}
      {error && <div className="message message--error">{error instanceof Error ? error.message : "No se pudo cargar el escritorio."}</div>}
      {aggregateScope ? accountOverview.data
        ? <BankAccountsOverview data={accountOverview.data} activeStage={overviewStage} />
        : accountOverview.isPending ? <div className="bank-accounts-overview__loading" role="status">Armando la mesa de cuentas…</div> : null
      : <>
      {view === "review" && balanceSummary.data && <BankBalanceBridge summary={balanceSummary.data} canEdit={user?.role === "ADMIN" || user?.role === "SUPERVISOR"} onEdit={() => setAnchorOpen(true)} />}
      {view === "imports" ? <ImportWorkspace config={importConfig} /> : view === "review" ? <ReviewWorkspace config={importConfig} workflow={workflow.data} run={run.data} hiddenEvidenceNames={["Patrones agrupados"]} /> : (
        (view === "reconciliation" || view === "data") && <>
          {view === "reconciliation" && (
            <>
              <Panel title="Conciliados automáticamente" meta={`${groups.data?.meta.total ?? 0} coincidencias`}>
                <div className="match-list match-list--compact">{groups.data?.items.length ? groups.data.items.map((group) => <MatchSpine key={group.id} group={group} variant="compact" maxApprovableDifference={workflow.data?.maxApprovableDifference} />) : <Empty>Ejecutá las reglas para obtener conciliaciones automáticas.</Empty>}</div>
                <Pagination meta={groups.data?.meta} onPageChange={(page) => setPage("groupPage", page)} />
              </Panel>
            </>
          )}
          <form className="manual-link-form" onSubmit={(event) => {
            event.preventDefault();
            if (manualSelectionValidation.valid && !manual.isPending) manual.mutate();
          }}>
            {view === "data" && <>
              <ValidationReadiness
                config={importConfig}
                workflow={workflow.data}
                toolbar={<div className="filter-bar bank-manual-filters" role="group" aria-label="Filtros de movimientos bancarios">
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      applyDataFilters({ q: search });
                    }}
                    placeholder="Descripción, referencia, cheque o monto"
                    aria-label="Buscar movimientos bancarios"
                  />
                  <select value={scope} onChange={(event) => applyDataFilters({ scope: event.target.value })} aria-label="Alcance temporal">
                    <option value="through">Todos los pendientes al cierre</option><option value="period">Pendientes del período</option><option value="prior">Pendientes de períodos anteriores</option>
                  </select>
                  <button type="button" className="button button--small" onClick={() => applyDataFilters({ q: search })}>Buscar</button>
                </div>}
                actions={<>
                  <span id="manual-reconcile-hint" className="sr-only" aria-live="polite">{manualSelectionValidation.message}</span>
                  <button
                    type="button"
                    className={`manual-selection-toggle manual-selection-toggle--${manualSelectionValidation.status.toLowerCase()}`}
                    aria-controls="manual-selection-balance"
                    aria-expanded={selectionSummaryOpen}
                    aria-label={manualSelectionToggleDescription}
                    onClick={() => setSelectionSummaryOpen((current) => !current)}
                  >
                    <span aria-hidden="true" />
                    <b>{manualSelectionToggleLabel}{manualSelectionValidation.status === "READY" && <BalancedCheckIcon className="manual-selection-toggle__check" />}</b>
                    <svg className="manual-selection-toggle__chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6" /></svg>
                  </button>
                  <span
                    className="manual-reconcile-control"
                    role={!manualSelectionValidation.valid ? "group" : undefined}
                    aria-label={!manualSelectionValidation.valid ? "Conciliación manual" : undefined}
                    aria-describedby={!manualSelectionValidation.valid ? "manual-reconcile-hint" : undefined}
                    tabIndex={!manualSelectionValidation.valid ? 0 : undefined}
                  >
                    <button
                      className="button button--primary manual-reconcile-button"
                      disabled={!manualSelectionValidation.valid || manual.isPending}
                      aria-describedby={!manualSelectionValidation.valid ? "manual-reconcile-hint" : undefined}
                    >
                      {manual.isPending ? "Conciliando..." : "Conciliar manualmente"}
                    </button>
                  </span>
                </>}
              />
              <div id="manual-selection-balance" hidden={!selectionSummaryOpen}>
                <ManualSelectionBalance
                  movements={selectedMovements}
                  ledgerEntries={selectedLedgerEntries}
                  movementTotals={movementSelectionTotals}
                  ledgerTotals={ledgerSelectionTotals}
                  validation={manualSelectionValidation}
                  currency={manualCurrency}
                  disabled={manual.isPending}
                  onRemoveMovement={(row) => setSelectedMovements((current) => toggleSelectedRow(current, row))}
                  onRemoveLedger={(row) => setSelectedLedgerEntries((current) => toggleSelectedRow(current, row))}
                  onClearMovements={() => setSelectedMovements(new Map())}
                  onClearLedger={() => setSelectedLedgerEntries(new Map())}
                  onClearAll={() => { setSelectedMovements(new Map()); setSelectedLedgerEntries(new Map()); setSelectionSummaryOpen(false); }}
                />
              </div>
            </>}
            <div className={`split-ledgers${view === "data" ? " split-ledgers--manual" : ""}`}>
              <BankRows title={view === "data" ? (scope === "period" ? "Movimientos bancarios del período" : scope === "prior" ? "Movimientos bancarios de períodos anteriores" : "Movimientos bancarios") : "Extracto"} page={movements.data} selected={view === "data" ? selectedMovements : undefined} showStatus={view === "data"} selectionDisabled={manual.isPending} onToggle={(row) => setSelectedMovements((current) => toggleSelectedRow(current, row))} onTogglePage={(rows) => setSelectedMovements((current) => toggleSelectedPage(current, rows))} onPage={(page) => setPage("movementPage", page)} />
              {view !== "data" && <div className="ledger-rule"><span>↔</span></div>}
              <BankRows title={view === "data" ? (scope === "period" ? "Libro Mayor pendiente del período" : scope === "prior" ? "Libro Mayor de períodos anteriores" : "Libro Mayor pendiente") : "Libro Mayor"} page={ledger.data} selected={view === "data" ? selectedLedgerEntries : undefined} showStatus={view === "data"} selectionDisabled={manual.isPending} onToggle={(row) => setSelectedLedgerEntries((current) => toggleSelectedRow(current, row))} onTogglePage={(rows) => setSelectedLedgerEntries((current) => toggleSelectedPage(current, rows))} onPage={(page) => setPage("ledgerPage", page)} />
            </div>
          </form>
        </>
      )}</>}
      </DomainShell>
      <BankAggregationRulesModal
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
      />
      <BankExportModal
        open={exportOpen}
        initialBankId={bankId}
        initialCurrency={currency}
        onClose={closeExport}
      />
      {view === "review" && balanceSummary.data && <BankBalanceAnchorModal open={anchorOpen} summary={balanceSummary.data} onClose={() => setAnchorOpen(false)} />}
    </>
  );
}

export function BankRows({ title, page, selected, showStatus, selectionDisabled = false, onToggle, onTogglePage, onPage }: { title: string; page?: PageResponse<MoneyRow>; selected?: ReadonlyMap<string, MoneyRow>; showStatus?: boolean; selectionDisabled?: boolean; onToggle(row: MoneyRow): void; onTogglePage(rows: MoneyRow[]): void; onPage(page: number): void }) {
  const selectPageRef = useRef<HTMLInputElement>(null);
  const visibleRows = page?.items ?? [];
  const selectedVisibleCount = selected ? visibleRows.filter((row) => selected.has(row.id)).length : 0;
  const allVisibleSelected = visibleRows.length > 0 && selectedVisibleCount === visibleRows.length;
  const totals = page?.totals;
  const currencyTotals = page?.currencyTotals?.length ? page.currencyTotals : [{ currency: (page?.items[0]?.currency ?? "ARS") as "ARS" | "USD", debit: totals?.debit ?? "0", credit: totals?.credit ?? "0" }];
  const summary = <span className="ledger-panel-summary"><b>{page?.meta.total ?? 0} movimientos</b>{currencyTotals.map((item) => <span className="ledger-panel-summary__currency" key={item.currency}><span>{item.currency} · Débito {money(item.debit, item.currency)}</span><span>Crédito {money(item.credit, item.currency)}</span></span>)}</span>;
  useEffect(() => {
    if (selectPageRef.current) selectPageRef.current.indeterminate = selectedVisibleCount > 0 && !allVisibleSelected;
  }, [allVisibleSelected, selectedVisibleCount]);
  return <Panel title={title} meta={summary} mobileCollapsible>
    {selected && <div className="transaction-selection-toolbar">
      <label className="check-field">
        <input
          ref={selectPageRef}
          type="checkbox"
          checked={allVisibleSelected}
          disabled={!visibleRows.length || selectionDisabled}
          onChange={() => onTogglePage(visibleRows)}
          aria-label={`Seleccionar esta página de ${title}`}
        />
        <span>Seleccionar esta página</span>
      </label>
      <span>{selectedVisibleCount} de {visibleRows.length} en esta página{selected.size > selectedVisibleCount ? ` · ${selected.size} en total` : ""}</span>
    </div>}
    <div className="transaction-list">
      {page?.items.map((row) => {
        const direction = Number(row.debit ?? 0) !== 0 ? "Débito" : Number(row.credit ?? 0) !== 0 ? "Crédito" : "Monto";
        return <article className={`transaction-row${selected?.has(row.id) ? " transaction-row--selected" : ""}`} key={row.id}>
          <div className="transaction-row__select">{selected && <input className="row-check" type="checkbox" checked={selected.has(row.id)} disabled={selectionDisabled} onChange={() => onToggle(row)} aria-label={`Seleccionar ${row.description || "movimiento"}`} />}</div>
          <div className="transaction-row__main">
            <strong>{row.description || "Sin descripción"}</strong>
            <div className="transaction-row__meta">
              <time className="mono" dateTime={row.transactionDate}>{shortDate(row.transactionDate)}</time>
              {row.voucherType && <span>Comprobante {row.voucherType}</span>}
              {row.reference && <span>Ref. {row.reference}</span>}
              {row.checkNumber && row.checkNumber !== row.reference && <span>Cheque {row.checkNumber}</span>}
            </div>
            {showStatus && <Provenance value={row.importBatch}/>}
          </div>
          <div className="transaction-row__amount"><span>{direction}</span><b className="mono">{money(rowAmount(row), row.currency)}</b></div>
          {showStatus && <div className="transaction-row__controls">
            <span className={`status status--${row.reconciliationStatus === "RECONCILED" || row.reconciled ? "success" : "warning"}`}>{row.reconciliationStatus === "RECONCILED" || row.reconciled ? "Conciliado" : "Pendiente"}</span>
            {row.periodRelation === "PRIOR" && <span className="status status--transit">Período anterior</span>}
          </div>}
        </article>;
      })}
    </div>
    {!page?.items.length && <Empty>No hay movimientos para estos filtros.</Empty>}
    <Pagination meta={page?.meta} onPageChange={onPage} />
  </Panel>;
}

export function BanksImportDetailPage() { return <DomainShell eyebrow="Extracto contra contabilidad" title="Bancos" description="Detalle y validación de una carga bancaria." basePath="/bancos" dataPath="movimientos" importConfig={importConfig} compactContext><ImportDetail config={importConfig} /></DomainShell>; }
